import type React from 'react';
import { cn } from '../../utils/cn.js';

interface FormSectionProps {
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/**
 * A titled group of fields: heading, then a two-column grid (one column below `md`). Wrap a field that must span the
 * grid in `FormFullWidth`. Template example: NextAdmin `profile/account/page.tsx` "Account Details" (`h2 mb-6 text-xl
 * leading-7 font-semibold`, `grid grid-cols-1 gap-5 md:grid-cols-2`) and demo `/form-layouts`.
 */
export function FormSection({ title, description, children, className }: FormSectionProps) {
  return (
    <section className={className}>
      <h2 className={cn('text-xl leading-7 font-semibold text-text-primary', description ? 'mb-1' : 'mb-6')}>
        {title}
      </h2>
      {description && <p className="mb-6 text-sm text-text-tertiary">{description}</p>}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">{children}</div>
    </section>
  );
}

/** A child of `FormSection` that spans both columns: a text area, a JSON field, a long select. */
export function FormFullWidth({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('col-span-1 md:col-span-2', className)}>{children}</div>;
}
